/**
 * Everything the AI-limit banner decides, as a pure function of its state and one event.
 *
 * The banner is a small state machine with four timers' worth of edge cases — a reset moment passing, a
 * dismissal expiring, a status read that failed, a burst of refusals from one batch run — and none of that
 * is worth discovering through a rendered component. The component keeps the effects (fetching, timers,
 * toasts); every DECISION lives here and is tested directly.
 */
export type AiRefusalNoticePhase = "idle" | "fetching" | "shown" | "dismissed";

export type AiRefusalNoticeState = {
  phase: AiRefusalNoticePhase;
  /** Epoch ms the pool comes back; null while unknown (status read failed, or nothing said blocked). */
  freesAt: number | null;
  /** When set, the banner hides itself at this moment — used only when no reset time is known. */
  autoHideAt: number | null;
  /** While dismissed, the moment the banner is allowed to come back. */
  suppressedUntil: number | null;
  /** When the status was last read, so a burst of refusals cannot become a burst of requests. */
  lastFetchedAt: number | null;
  /**
   * Bumped by every `tick`, including the ones that change nothing else.
   *
   * The timer is one-shot and re-armed from state, so a tick that returned the SAME state object made
   * React bail out of the render — leaving the deps unchanged and no replacement timer scheduled. A
   * callback firing even 1 ms early (a backward clock step, resume from suspend) therefore stranded the
   * band on a stale reset time until it was dismissed or the page reloaded. Widest for the weekly
   * window, where the timeout is armed for days.
   */
  tickSeq: number;
  /**
   * True while a re-read is running UNDER a band that is already up (the reset moment arrived and we are
   * checking whether the pool really freed). The band stays on screen for it: hiding during the round
   * trip dropped the 44px it reserves, so every screen behind it jumped up and then back down for no
   * reason the user could see.
   */
  refreshing: boolean;
};

export type AiRefusalNoticeEvent =
  | { type: "limitRefusal" }
  | { type: "statusResolved"; freesAt: number | null }
  | { type: "statusFailed" }
  | { type: "dismiss" }
  | { type: "tick" }
  /** The session ended: forget everything, including a standing dismissal. */
  | { type: "reset" };

/**
 * What the component must DO after a transition. The reducer never performs it itself.
 *
 * Only the limit refusal is modelled here. A busy model and spent learner points are NOT: they are said
 * where they happen (the chat bubble, the screen's own toast) and they clear on their own, so there is
 * no state to keep and nothing to dismiss. Routing them through this machine would also mean returning
 * an unchanged state object, which React treats as "nothing happened".
 */
export type AiRefusalNoticeEffect = "fetchStatus" | "none";

export type AiRefusalNoticeResult = {
  state: AiRefusalNoticeState;
  effect: AiRefusalNoticeEffect;
};

export const INITIAL_AI_REFUSAL_NOTICE_STATE: AiRefusalNoticeState = {
  phase: "idle",
  freesAt: null,
  autoHideAt: null,
  suppressedUntil: null,
  lastFetchedAt: null,
  refreshing: false,
  tickSeq: 0,
};

/**
 * How long the banner stays up when nobody can tell it when the pool frees.
 *
 * A refusal happened, so saying nothing is wrong; but a banner with no reset time and no expiry would sit
 * there until the user dismissed it, long after the pool recovered.
 */
export const NO_TIME_VISIBLE_MS = 2 * 60 * 1000;

/**
 * How long a dismissal lasts when the reset moment is unknown.
 *
 * With a known reset the dismissal naturally lasts until then. Without one, "dismiss" has to mean
 * something finite, or the user's next refusal an hour later would be silent.
 */
const DISMISS_FALLBACK_MS = 10 * 60 * 1000;

/**
 * The shortest gap between two status reads while the banner is up without a time.
 *
 * A blocked pool refuses every concurrent worker at once — the animation and presentation screens run
 * three at a time, and the JSONL converter loops three times — so one action can raise several refusals in
 * the same second. Without this the banner would answer each one with its own HTTP request.
 */
const REFETCH_COOLDOWN_MS = 30 * 1000;

const showWithFreesAt = (
  state: AiRefusalNoticeState,
  freesAt: number | null,
  now: number,
): AiRefusalNoticeState => ({
  ...state,
  phase: "shown",
  freesAt,
  // A known reset time IS the expiry, so no auto-hide is needed; without one, the banner must expire.
  autoHideAt: freesAt === null ? now + NO_TIME_VISIBLE_MS : null,
  suppressedUntil: null,
  lastFetchedAt: now,
  refreshing: false,
});

/**
 * Whether the bar is on screen right now.
 *
 * A re-check under an already-visible band counts as visible: the band is still telling the truth while
 * it confirms, and blinking it off for one round trip moved every screen behind it.
 */
export const isAiRefusalNoticeVisible = (state: AiRefusalNoticeState): boolean =>
  state.phase === "shown" || (state.phase === "fetching" && state.refreshing);

export const reduceAiRefusalNotice = (
  state: AiRefusalNoticeState,
  event: AiRefusalNoticeEvent,
  now: number = Date.now(),
): AiRefusalNoticeResult => {
  switch (event.type) {
    case "limitRefusal": {
      if (state.phase === "dismissed") {
        const suppressedUntil = state.suppressedUntil;
        if (suppressedUntil !== null && now < suppressedUntil) {
          return { state, effect: "none" };
        }
        return {
          state: { ...state, phase: "fetching", lastFetchedAt: now },
          effect: "fetchStatus",
        };
      }
      if (state.phase === "shown") {
        // Already saying the right thing. Re-read only if the time is still unknown and the cooldown has
        // passed, so a batch of refusals cannot turn into a batch of requests.
        const isStale =
          state.freesAt === null &&
          (state.lastFetchedAt === null || now - state.lastFetchedAt >= REFETCH_COOLDOWN_MS);
        if (!isStale) return { state, effect: "none" };
        return {
          state: { ...state, phase: "fetching", lastFetchedAt: now, refreshing: true },
          effect: "fetchStatus",
        };
      }
      if (state.phase === "fetching") return { state, effect: "none" };
      return {
        state: { ...state, phase: "fetching", lastFetchedAt: now },
        effect: "fetchStatus",
      };
    }
    case "statusResolved": {
      // Reached both from the first read and from the re-read after a reset moment passed. A null
      // `freesAt` here means the server named no blocked window — the pool may well have recovered — but
      // a refusal DID happen, so the banner still shows, just without a time.
      //
      // Gated on `fetching`, which is stricter than the `dismissed` check it replaces and subsumes it:
      // both status events are ONLY ever dispatched from the effect that runs in `fetching`, and the
      // state can leave that phase while a read is still in flight — a dismiss, a session reset, or the
      // immediate tick that follows either. A late answer then re-showed the band one round trip after
      // the ✕ was pressed, and on the login screen after a logout, carrying the previous session's
      // reset time and re-reserving its 44px.
      if (state.phase !== "fetching") return { state, effect: "none" };
      return { state: showWithFreesAt(state, event.freesAt, now), effect: "none" };
    }
    case "statusFailed": {
      // The status call is best-effort: it can 423 on a token with no profile pair, or simply fail. The
      // refusal is still real, so the banner appears with the no-time copy rather than being swallowed.
      // Same phase gate as above, for the same reason.
      if (state.phase !== "fetching") return { state, effect: "none" };
      return { state: showWithFreesAt(state, null, now), effect: "none" };
    }
    case "dismiss": {
      // Visibility covers a re-check under an already-shown band, so the gate here has to match it
      // EXACTLY. It did not: after visibility was widened the ✕ rendered live and enabled during the
      // round trip while this case bailed out, so the click was not deferred but DISCARDED — the
      // status answer then re-showed the band with suppressedUntil cleared. Any state the band is
      // visible in must be dismissible from.
      if (!isAiRefusalNoticeVisible(state)) return { state, effect: "none" };
      return {
        state: {
          ...state,
          phase: "dismissed",
          autoHideAt: null,
          // Cleared, or the band re-appears the moment the phase turns `fetching` again — visibility
          // covers `fetching && refreshing`, and this flag outlived the dismissal that ended its band.
          refreshing: false,
          // Hold the dismissal until the pool actually frees; with no known reset — or a reset that has
          // ALREADY PASSED, which is exactly the state a dismiss-during-re-check is in, since a passed
          // reset is what triggered the re-check — a fixed window instead. Using the past moment made
          // the dismissal expire on the very next tick, so it bought nothing.
          suppressedUntil:
            state.freesAt !== null && state.freesAt > now
              ? state.freesAt
              : now + DISMISS_FALLBACK_MS,
        },
        effect: "none",
      };
    }
    case "reset":
      return { state: { ...INITIAL_AI_REFUSAL_NOTICE_STATE }, effect: "none" };
    case "tick": {
      // Every tick produces a NEW state object with a NEW tickSeq, so the effect that armed this timer
      // re-runs and re-arms even when the tick changed nothing else. `tickSeq` is in that effect's
      // dependency array — without it the new object alone changes nothing, because the four fields the
      // effect reads are identical and it never tears down.
      const ticked: AiRefusalNoticeState = { ...state, tickSeq: state.tickSeq + 1 };
      if (ticked.phase === "shown") {
        // The reset moment arrived: do not just hide, CHECK. The weekly window can still be blocking, and
        // a fresh refusal after a silent hide would look like the banner never worked.
        if (ticked.freesAt !== null && now >= ticked.freesAt) {
          return {
            state: { ...ticked, phase: "fetching", lastFetchedAt: now, refreshing: true },
            effect: "fetchStatus",
          };
        }
        if (ticked.autoHideAt !== null && now >= ticked.autoHideAt) {
          return { state: { ...INITIAL_AI_REFUSAL_NOTICE_STATE }, effect: "none" };
        }
        return { state: ticked, effect: "none" };
      }
      if (ticked.phase === "dismissed") {
        if (ticked.suppressedUntil !== null && now >= ticked.suppressedUntil) {
          return { state: { ...INITIAL_AI_REFUSAL_NOTICE_STATE }, effect: "none" };
        }
        return { state: ticked, effect: "none" };
      }
      return { state: ticked, effect: "none" };
    }
    default:
      return { state, effect: "none" };
  }
};
